import React, { useContext, useState } from 'react'
import { IBreadcrumb } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'

// components
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb'
import Reservation from './components/Reservation/Reservation'
import CancelReservationModal from './components/CancelReservationModal/CancelReservationModal'
import OverlayLoading from '../../atoms/OverlayLoading/OverlayLoading'
import ReservationBookingModal from '../../components/ReservationBookingModal/ReservationBookingModal'
import MyReservationPageHead from './components/MyReservationPageHead/MyReservationPageHead'

// types
import {
	ConfigResponse,
	SalonServicesResponse,
	ReservationResponse,
	MyReservationPageQuery,
	ReservationBookingModalInitData,
	CmsMyReservationPageData,
	PageProps
} from '../../types/types'

// utils
import createPageContainer from '../../utils/createPageContainer'
import { AppContext } from '../../utils/appProvider'
import { getLinkAttributes } from '../../utils/helper'

// hooks
import useMessages from '../../hooks/useMessages'

// styles
import * as SC from './MyReservationPageStyles'

type RouteProps = {
	reservationID: string
	validQueryParamsToken: string | undefined
}

type MyReservationPageData = {
	reservationData: ReservationResponse
	configData: ConfigResponse
	myReservationPageCmsData: CmsMyReservationPageData
	salonServicesData: SalonServicesResponse | undefined
}

type MyReservationPageProps = PageProps<MyReservationPageData, MyReservationPageQuery, RouteProps>

const MyReservationPage = (props: MyReservationPageProps) => {
	const { reservationID, salonServicesData, configData, validQueryParamsToken, reservationData, setPageData, myReservationPageCmsData, isFetching } = props
	const { image: promoBanner } = myReservationPageCmsData.reservationDetailBannerData

	const { messages } = useMessages()
	const { locale } = useIntl()
	const {
		apiBrowser,
		isAuthorized,
		productionEnvironment,
		userInfo: { firstName, lastName, email }
	} = useContext(AppContext)

	const [reservationBookingModalInitData, setReservationBookingModalInitData] = useState<ReservationBookingModalInitData | null>(null)
	const [cancelReservationModalData, setCancelReservationModalData] = useState<{ reservationID: string } | null>()

	const fetchReservationData = async () => {
		// NOTE: token in query parameters are only for guest users, requests for authorized users already have token in headers, which is handled on the app level axios settings
		const config = isAuthorized
			? undefined
			: {
					headers: {
						Authorization: `Bearer ${validQueryParamsToken}`
					}
				}

		setPageData({ dataKey: 'reservationData', data: () => apiBrowser.b2c.getReservationData(reservationID, undefined, config) })
	}

	const onCancelReservation = () => {
		setCancelReservationModalData({ reservationID })
	}

	const onCancelReservationSubmitSuccess = () => {
		fetchReservationData()
		setCancelReservationModalData(null)
	}

	const onBookAgain = (reservation: NonNullable<ReservationResponse['reservation']>) => {
		const { service, employee, serviceCategoryParameterValue } = reservation.calendarEvent
		const originalParameterValueID = serviceCategoryParameterValue?.id
		const isOriginalParameterStillAvailable = Boolean(
			originalParameterValueID && service?.serviceCategoryParameter?.values.some((value) => value.id === originalParameterValueID)
		)
		const isVisitor = Boolean(reservation.customer?.isVisitor)

		const reservationModalInitData: ReservationBookingModalInitData = {
			initIndustryID: null,
			initServiceID: service?.id ?? null,
			initAvResTimeSlotDayPart: null,
			initServiceCategoryParameterID: service?.useCategoryParameter && service?.serviceCategoryParameter?.id ? service.serviceCategoryParameter.id : null,
			parameterSelectionData:
				isOriginalParameterStillAvailable && originalParameterValueID ? { serviceCategoryParameterValueID: originalParameterValueID } : null,
			employeeAndDateSelectionData: {
				employeeID: employee.id
			},
			employeeAndDateGroupSelectionData: {
				employeeID: employee.id
			},
			bookingConfirmationData: {
				...(isAuthorized
					? {
							firstName,
							lastName,
							email
						}
					: {}),
				isVisitor,
				visitor:
					isVisitor && reservation.customer?.firstName && reservation.customer?.lastName
						? { firstName: reservation.customer.firstName, lastName: reservation.customer.lastName }
						: undefined,
				phone:
					reservation.customer?.phone && reservation.customer?.phonePrefixCountryCode
						? {
								phoneNumber: reservation.customer.phone,
								phonePrefixCountryCode: reservation.customer.phonePrefixCountryCode
							}
						: undefined
			},
			reservationsSummaryData: null
		}
		setReservationBookingModalInitData(reservationModalInitData)
	}

	const breadcrumbItems: IBreadcrumb[] = [
		/* { link: PAGE_LINKS['/salons/my-reservations'](locale), name: messages['My reservations'] */ { name: messages['Reservation detail'], link: '' }
	]

	// NOTE: only desktop version
	const promoBannerImage = <SC.PromoBannerImage src={promoBanner.desktop} alt={promoBanner.alt} />

	const promoBannerElement = promoBanner.desktopWebP ? (
		<picture>
			<source srcSet={promoBanner.desktopWebP} type='image/webp' />
			{promoBannerImage}
		</picture>
	) : (
		promoBannerImage
	)

	return (
		<>
			<MyReservationPageHead reservation={reservationData.reservation} />

			<SC.PageContainer>
				<Breadcrumb breadcrumbItems={breadcrumbItems} />
				<SC.MainContent>
					<SC.ReservationContainer>
						<OverlayLoading isLoading={isFetching} announceStatus>
							<Reservation
								reservation={reservationData.reservation}
								queryParamsToken={validQueryParamsToken}
								salonServicesData={salonServicesData}
								onCancelReservation={onCancelReservation}
								onBookAgain={onBookAgain}
							/>
						</OverlayLoading>
					</SC.ReservationContainer>
					<SC.SidebarContainer>
						{promoBanner.link ? (
							<SC.PromoBannerLink href={promoBanner.link} {...getLinkAttributes(promoBanner.link, productionEnvironment, locale)}>
								{promoBannerElement}
							</SC.PromoBannerLink>
						) : (
							promoBannerElement
						)}
					</SC.SidebarContainer>
				</SC.MainContent>
			</SC.PageContainer>
			{reservationBookingModalInitData && salonServicesData && (
				<ReservationBookingModal
					salonData={{
						phones: reservationData.reservation.calendarEvent.salon.phones,
						id: reservationData.reservation.calendarEvent.salon.id,
						availableReservationSystem: true,
						images: reservationData.reservation.calendarEvent.salon.images
					}}
					isOpen
					initData={reservationBookingModalInitData}
					onClose={() => setReservationBookingModalInitData(null)}
					salonServicesData={salonServicesData}
					configData={configData}
				/>
			)}
			{cancelReservationModalData && (
				<CancelReservationModal
					isOpen
					queryParamsToken={validQueryParamsToken}
					reservationID={cancelReservationModalData.reservationID}
					reservationCancellationQuickFeedbackAnswers={configData.reservationCancellationQuickFeedbackAnswers}
					onClose={() => setCancelReservationModalData(null)}
					onSubmitSuccess={onCancelReservationSubmitSuccess}
				/>
			)}
		</>
	)
}

// page container
const MyReservationPageContainer = createPageContainer<MyReservationPageData, MyReservationPageQuery, RouteProps>({
	identifier: 'MyReservationPage',
	PageComponent: MyReservationPage,
	initDefaultData: async ({ api, reservationID, validQueryParamsToken, isAuthorized }) => {
		// NOTE: token in query parameters are only for guest users, requests for authorized users already have token in headers, which is handled on the app level axios settings
		const config = isAuthorized
			? undefined
			: {
					headers: {
						Authorization: `Bearer ${validQueryParamsToken}`
					}
				}
		const [reservationData, configData, myReservationPageCmsData] = await Promise.all([
			api.b2c.getReservationData(reservationID, undefined, config),
			api.b2c.getConfigData(),
			api.cms.getMyReservationPageCmsData()
		])

		let salonServicesData: SalonServicesResponse | undefined

		if (reservationData.reservation?.calendarEvent.salon.id) {
			salonServicesData = await api.b2c.getSalonServicesData(reservationData.reservation.calendarEvent.salon.id).catch(() => undefined)
		}

		return {
			reservationData,
			configData,
			myReservationPageCmsData,
			salonServicesData
		}
	}
})

export default MyReservationPageContainer
